import type { RefObject } from 'react';
import { useEffect, useId, useRef } from 'react';

import { useTracking } from '@change-corgi/core-react/tracking';

import { useStartAPetitionPageState } from '#src/app/pages/startAPetitionV2/pageContext';
import { useScrollContainer, useScrollEnd } from '#src/app/shared/hooks/carousel';

type UseDecisionMakerCarouselReturn = ModelHookResult<
	{
		listRef: RefObject<HTMLUListElement | null>;
		/** The slide ids end in their index, which is how the shared scroll hook reads the card in view. */
		slideIdPrefix: string;
		/** Zero-based index of the card in view. */
		currentIndex: number;
		/** A single card has nowhere to move, so it gets no arrows or counter. */
		navigationShown: boolean;
		previousDisabled: boolean;
		nextDisabled: boolean;
	},
	{
		showPrevious: () => void;
		showNext: () => void;
	}
>;

/**
 * A native scroll-snap scroller, so swiping comes from the browser; the arrows scroll it and stop at
 * the ends. `total` is never 0: the container only renders the carousel when there are decision
 * makers to show.
 */
export function useDecisionMakerCarousel({ total }: { total: number }): UseDecisionMakerCarouselReturn {
	const {
		data: { normalizedStep: step },
	} = useStartAPetitionPageState();
	const listRef = useRef<HTMLUListElement>(null);
	const slideIdPrefix = useId();
	const { data, actions } = useScrollContainer(listRef);
	const { handleScrollEnd } = actions;

	// stays on the last card when the one at the end is removed
	const currentIndex = Math.min(data.activeIndex, total - 1);

	const track = useTracking();
	useScrollEnd(listRef, handleScrollEnd);

	// a Remove changes how far the list scrolls without a scroll event, so the arrows are rechecked
	useEffect(() => {
		handleScrollEnd();
	}, [handleScrollEnd, total]);

	const previousTotal = useRef(total);
	const previousIndex = useRef(currentIndex);
	useEffect(() => {
		const removed = total < previousTotal.current;
		const moved = currentIndex !== previousIndex.current;
		previousTotal.current = total;
		previousIndex.current = currentIndex;

		// a Remove of the card at the end also moves the card in view, which isn't the starter moving
		if (removed) return;
		// by arrow or swipe
		if (moved) void track('sap_dm_carousel_navigate', { step, position: currentIndex, dm_count: total });
	}, [currentIndex, step, total, track]);

	return {
		data: {
			listRef,
			slideIdPrefix,
			currentIndex,
			navigationShown: total > 1,
			previousDisabled: data.scrollLeftDisabled,
			nextDisabled: data.scrollRightDisabled,
		},
		actions: {
			showPrevious: actions.scrollToLeft,
			showNext: actions.scrollToRight,
		},
	};
}
